// material-ui
import { createTheme } from "@mui/material/styles";

// fonts
import DazzedBold from "../assets/fonts/Dazzed-Bold.ttf";
import DazzedHeavy from "../assets/fonts/Dazzed-Heavy.ttf";
import DazzedMedium from "../assets/fonts/Dazzed-Medium.ttf";
import DazzedRegular from "../assets/fonts/Dazzed-Regular.ttf";
import DazzedSemibold from "../assets/fonts/Dazzed-SemiBold.ttf";

import AlpinaBold from "../assets/fonts/GT-Alpina-Condensed-Bold.ttf";

// third-party
import { PalettesProps, presetPalettes } from "@ant-design/colors";

export const createExtraordinaryTheme = () => {
  const colors = presetPalettes;

  const greyPrimary = [
    "#ffffff",
    "#fafafa",
    "#f5f5f5",
    "#f0f0f0",
    "#d9d9d9",
    "#bfbfbf",
    "#8c8c8c",
    "#595959",
    "#262626",
    "#141414",
    "#000000"
  ];

  const greyAscent = ["#fafafa", "#bfbfbf", "#434343", "#1f1f1f"];
  const greyConstant = ["#fafafb", "#e6ebf1"];

  colors.grey = [...greyPrimary, ...greyAscent, ...greyConstant];

  const paletteColor = extraordinaryThemeOptions(colors);

  return createTheme({
    palette: {
      common: {
        black: "#000",
        white: "#fff"
      },
      ...paletteColor,
      text: {
        primary: paletteColor.grey[700],
        secondary: paletteColor.grey[500],
        disabled: paletteColor.grey[400]
      },
      action: {
        disabled: paletteColor.grey[300]
      },
      divider: paletteColor.grey[200],
      background: {
        paper: paletteColor.grey[0],
        default: paletteColor.grey.A50
      }
    },
    typography: {
      fontFamily: "'Dazzed', sans-serif",
      h2: {
        fontSize: "46px",
        fontWeight: 700
      }
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "capitalize",
            fontFamily: "'GT-Alpina-Condensed', 'Dazzed', sans-serif",
            fontWeight: 700,
            borderRadius: 6,
            height: 45
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: 6,
            height: 45
          },
          notchedOutline: {
            fontWeight: 500,
            borderRadius: 6
          }
        }
      },
      MuiCssBaseline: {
        styleOverrides: `
          @font-face {
            font-family: 'Dazzed';
            src: local('Dazzed'), local('Dazzed-Regular'), url(${DazzedRegular}) format('truetype');
            font-weight: 400;
          }
          @font-face {
            font-family: 'Dazzed';
            src: local('Dazzed'), local('Dazzed-Medium'), url(${DazzedMedium}) format('truetype');
            font-weight: 500;
          }
          @font-face {
            font-family: 'Dazzed';
            src: local('Dazzed'), local('Dazzed-SemiBold'), url(${DazzedSemibold}) format('truetype');
            font-weight: 600;
          }
          @font-face {
            font-family: 'Dazzed';
            src: local('Dazzed'), local('Dazzed-Bold'), url(${DazzedBold}) format('truetype');
            font-weight: 700;
          }
          @font-face {
            font-family: 'Dazzed';
            src: local('Dazzed'), local('Dazzed-Heavy'), url(${DazzedHeavy}) format('truetype');
            font-weight: 900;
          }
          @font-face {
            font-family: 'GT-Alpina-Condensed';
            src: local('GT-Alpina-Condensed'), url(${AlpinaBold}) format('woff');
          }
        `
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "5px"
          },
          colorPrimary: {
            backgroundColor: "#015450"
          },
          colorSecondary: {
            backgroundColor: "#00ABA3"
          },
          colorError: {
            backgroundColor: "#FC806F"
          },
          colorWarning: {
            backgroundColor: "#808080"
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "20px"
          }
        }
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: "34px"
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            paddingLeft: "26px"
          }
        }
      }
    }
  });
};

const extraordinaryThemeOptions = (colors: PalettesProps) => {
  const { grey } = colors;
  const greyColors = {
    0: grey[0],
    50: grey[1],
    100: grey[2],
    200: grey[3],
    300: grey[4],
    400: grey[5],
    500: grey[6],
    600: grey[7],
    700: grey[8],
    800: grey[9],
    900: grey[10],
    A50: grey[15],
    A100: grey[11],
    A200: grey[12],
    A400: grey[13],
    A700: grey[14],
    A800: grey[16]
  };
  const contrastText = "#fff";

  // These are just the standard healthnow colors, in future these will be new extraordinary colors
  const primaryColors = [
    "#050505",
    "#050505",
    "#050505",
    "#050505",
    "#050505",
    "#050505", // Black
    "#050505",
    "#050505",
    "#050505",
    "#050505"
  ];

  const errorColors = ["#FDE8E7", "#F25E52", "#F04134", "#EE3B2F", "#E92A21"];
  const warningColors = ["#FFF7E0", "#FFC926", "#FFBF00", "#FFB900", "#FFA900"];
  const infoColors = ["#E0F4F5", "#26B0BA", "#00A2AE", "#009AA7", "#008694"];
  const successColors = ["#E0F5EA", "#26B56E", "#00A854", "#00A04D", "#008D3A"];

  return {
    ...extraordinaryColors,
    primary: {
      lighter: primaryColors[0],
      100: primaryColors[1],
      200: primaryColors[2],
      light: primaryColors[3],
      400: primaryColors[4],
      main: primaryColors[5],
      dark: primaryColors[6],
      700: primaryColors[7],
      darker: primaryColors[8],
      900: primaryColors[9],
      contrastText
    },
    secondary: {
      lighter: greyColors[100],
      100: greyColors[100],
      200: greyColors[200],
      light: greyColors[300],
      400: greyColors[400],
      main: greyColors[500],
      600: greyColors[600],
      dark: greyColors[700],
      800: greyColors[800],
      darker: greyColors[900],
      A100: greyColors[0],
      A200: greyColors.A400,
      A300: greyColors.A700,
      contrastText: greyColors[0]
    },
    error: {
      lighter: errorColors[0],
      light: errorColors[1],
      main: errorColors[2],
      dark: errorColors[3],
      darker: errorColors[4],
      contrastText
    },
    warning: {
      lighter: warningColors[0],
      light: warningColors[1],
      main: warningColors[2],
      dark: warningColors[3],
      darker: warningColors[4],
      contrastText: greyColors[100]
    },
    info: {
      lighter: infoColors[0],
      light: infoColors[1],
      main: infoColors[2],
      dark: infoColors[3],
      darker: infoColors[4],
      contrastText
    },
    success: {
      lighter: successColors[0],
      light: successColors[1],
      main: successColors[2],
      dark: successColors[3],
      darker: successColors[4],
      contrastText
    },
    grey: greyColors
  };
};

export const extraordinaryColors = {
  green: { light: "#88D76C", main: "#03B07D" },
  blue: { light: "#CFE9EF", main: "#97CFDC" },
  deepBlue: { main: "#0279B0" },
  red: { main: "#E65654" },
  tomato: { main: "#C42231" },
  orange: { light: "rgba(253,134,79, 0.1)", main: "#FD864F" },
  purple: { main: "#A46FE0" },
  yellow: { light: "#F4FD7A", main: "#E6A236" },
  pink: { main: "#FD4F7B", light: "#FFE0E8" },
  white: { main: "#FFFFFF" }
};
